/**
 * Subir a planilha de disponibilidade.
 *
 * **A tela existe para substituir uma semana de digitação.** Trezentos e seis
 * professores respondem um questionário por link; hoje alguém relê tudo e
 * redigita campo por campo, no sistema de grade. São 12.240 células.
 *
 * **O arquivo não é gravado quando sobe.** Soltar a planilha produz um
 * retrato — quem foi reconhecido, quem não foi, o que o importador se recusou
 * a adivinhar — e nada mais. Gravar é um segundo ato, explícito, e isso é
 * deliberado: uma importação errada de disponibilidade deixa professor sem
 * horário no semestre inteiro, e o sintoma só aparece lá na frente, como
 * grade inviável sem culpado óbvio.
 *
 * **A ordem da tela é a ordem do trabalho**, e ela é o contrário da ordem do
 * tamanho. Primeiro o que exige decisão — quem não foi encontrado, quem está
 * ambíguo, quem respondeu em branco —, e por último as 291 respostas que
 * deram certo, fechadas. Pôr o sucesso no topo faria a pessoa rolar duzentas
 * linhas para achar as quinze que são o trabalho dela.
 *
 * **O texto livre tem seção própria.** Quatro em cada dez respostas trazem
 * observação escrita à mão, e ali moram restrições que o formulário não tinha
 * como capturar — *"deixei a tarde livre porque é quando temos reunião de
 * eixo"*. Nada disso é aplicado automaticamente. Fica à vista, com o nome de
 * quem escreveu, porque o contrário é apagar informação que a pessoa se deu
 * ao trabalho de dar.
 */

import { useCallback, useRef, useState } from 'react'

import {
  ErroApi,
  subirDisponibilidade,
  type Previa,
  type RespostaImportada,
} from '../api'
import { Icon } from '../components/Icon'

const DIAS = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo']

/** `450` → `7h30`. O servidor fala em minuto do dia. */
function hora(minutos: number): string {
  const h = Math.floor(minutos / 60)
  const m = minutos % 60
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`
}

/**
 * A tabela de respostas, com uma coluna final que cada seção decide.
 *
 * Genérica no tipo do item para que a coluna extra receba a linha inteira já
 * tipada — sem isso, cada seção reencontraria pelo número da linha o item que
 * já tinha em mãos.
 */
function Linhas<T extends RespostaImportada>({
  itens,
  coluna,
  children,
}: {
  itens: T[]
  coluna?: string
  children?: (r: T) => React.ReactNode
}) {
  return (
    <table className="comparar">
      <thead>
        <tr>
          <th scope="col">Linha</th>
          <th scope="col">Quem respondeu</th>
          <th scope="col">Matrícula</th>
          <th scope="col">Marcou</th>
          {children && <th scope="col">{coluna ?? 'No cadastro'}</th>}
        </tr>
      </thead>
      <tbody>
        {itens.map((r) => (
          <tr key={r.linha}>
            <td>{r.linha}</td>
            <td>
              {r.nome}
              {r.anteriores.length > 0 && (
                <div
                  className="field__hint"
                  title={`Respostas anteriores nas linhas ${r.anteriores.join(', ')}, que ficaram de fora`}
                >
                  respondeu {r.anteriores.length + 1} vezes
                  {r.respondida_em &&
                    `; vale a de ${new Date(r.respondida_em).toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit' })}`}
                </div>
              )}
            </td>
            <td className="input--mono">{r.matricula ?? '—'}</td>
            <td>{r.marcacoes}</td>
            {children && <td>{children(r)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function Importar({ projeto }: { projeto: string }) {
  const [previa, setPrevia] = useState<Previa | null>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [sobreposto, setSobreposto] = useState(false)
  const entrada = useRef<HTMLInputElement>(null)

  const enviar = useCallback(
    async (f: File, aplicar: boolean) => {
      setOcupado(true)
      setErro(null)
      try {
        setPrevia(await subirDisponibilidade(projeto, f, aplicar))
        setArquivo(f)
      } catch (e) {
        setErro(e instanceof ErroApi ? e.message : 'não foi possível ler a planilha')
        if (!aplicar) setPrevia(null)
      } finally {
        setOcupado(false)
      }
    },
    [projeto],
  )

  const gravado = previa && previa.aplicado !== false ? previa.aplicado : null
  const semMarcacao = previa?.avisos.filter((a) => a.tipo === 'sem_marcacao') ?? []
  const comObservacao = [...(previa?.casadas ?? []), ...(previa?.conferir ?? [])].filter(
    (r) => r.observacao,
  )

  return (
    <>
      <header className="page-head">
        <h1>Importar disponibilidade</h1>
        <p className="page-head__note">
          A planilha do questionário, como ela sai. Nada é gravado até você mandar.
        </p>
      </header>

      <div
        className={`solta${sobreposto ? ' solta--ativa' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setSobreposto(true)
        }}
        onDragLeave={() => setSobreposto(false)}
        onDrop={(e) => {
          e.preventDefault()
          setSobreposto(false)
          const f = e.dataTransfer.files[0]
          if (f) void enviar(f, false)
        }}
      >
        <Icon name="importar" size={20} />
        <p>
          Solte aqui a planilha de disponibilidade, ou{' '}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => entrada.current?.click()}
            disabled={ocupado}
          >
            escolha o arquivo
          </button>
        </p>
        <p className="field__hint">
          O export do questionário, em <code>.xlsx</code>. Não precisa preparar nada antes.
        </p>
        <input
          ref={entrada}
          type="file"
          accept=".xlsx"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void enviar(f, false)
            e.target.value = ''
          }}
        />
      </div>

      {ocupado && <p className="field__hint">Lendo…</p>}

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {previa && (
        <>
          {gravado ? (
            <p className="state state--ok" role="status">
              <Icon name="ok" size={14} />
              Gravado: {gravado.professores} professores, {gravado.janelas} horários marcados como
              indisponíveis.
              {gravado.ignoradas_sem_marcacao > 0 &&
                ` ${gravado.ignoradas_sem_marcacao} resposta(s) em branco ficaram de fora.`}
            </p>
          ) : (
            <div className="section__head">
              <h2>
                {previa.resumo.casadas} de {previa.resumo.respostas} respostas prontas para gravar
              </h2>
              <button
                type="button"
                className="btn btn--primary"
                disabled={ocupado || previa.resumo.casadas === 0}
                onClick={() => arquivo && void enviar(arquivo, true)}
              >
                Gravar {previa.resumo.janelas} horários
              </button>
            </div>
          )}

          <p className="field__hint">
            A planilha declara {previa.tempos.length} horários por dia, de {DIAS[previa.dias[0] ?? 1]}{' '}
            a {DIAS[previa.dias[previa.dias.length - 1] ?? 5]}:{' '}
            {previa.tempos.map((t) => `${hora(t.inicio)}–${hora(t.fim)}`).join(' · ')}
          </p>

          {/* --- primeiro o que exige decisão --- */}

          {previa.ambiguas.length > 0 && (
            <section className="section">
              <h3>
                {previa.ambiguas.length} com mais de um professor possível — não escolhemos por você
              </h3>
              <Linhas itens={previa.ambiguas} coluna="Pode ser">
                {(r) => r.candidatos.map((c) => c.nome).join(' ou ')}
              </Linhas>
            </section>
          )}

          {previa.disputados.length > 0 && (
            <section className="section">
              <h3>{previa.disputados.length} responderam mais de uma vez</h3>
              <p className="field__hint">
                Duas respostas chegaram para a mesma pessoa. Gravar as duas somaria horários que se
                contradizem e produziria uma disponibilidade que <strong>nenhuma das respostas
                declarou</strong>; escolher uma seria escolher por você. Apague a errada na planilha
                e suba de novo.
              </p>
              <ul className="stack">
                {previa.disputados.map((d) => (
                  <li key={d.professor_id} className="callout">
                    <strong>{d.professor_nome}</strong>
                    <p>
                      {d.respostas
                        .map((r) => `linha ${r.linha}: ${r.nome} marcou ${r.marcacoes}`)
                        .join(' · ')}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {previa.conferir.length > 0 && (
            <section className="section">
              <h3>{previa.conferir.length} com o nome abreviado — confira antes</h3>
              <p className="field__hint">
                Primeiro e último nome batem, o meio está abreviado. É quase certo que seja a mesma
                pessoa, e <strong>quase certo não grava</strong>: corrija o nome no cadastro ou na
                planilha e suba de novo.
              </p>
              <Linhas itens={previa.conferir}>{(r) => r.professor_nome}</Linhas>
            </section>
          )}

          {semMarcacao.length > 0 && (
            <section className="section">
              <h3>{semMarcacao.length} responderam sem marcar horário nenhum</h3>
              <p className="field__hint">
                Não importamos a disponibilidade dessas respostas. Aplicá-las ao pé da letra
                deixaria essas pessoas sem poder dar aula no semestre inteiro, e tratá-las como
                livres inventaria uma declaração que ninguém fez.
              </p>
              <ul className="stack">
                {semMarcacao.map((a) => (
                  <li key={a.linha} className="state state--pendencia">
                    <Icon name="pendencia" size={14} />
                    {a.mensagem}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {previa.sem_cadastro.length > 0 && (
            <section className="section">
              <h3>{previa.sem_cadastro.length} responderam e não estão no cadastro</h3>
              <p className="field__hint">
                Pode ser gente nova, ou nome digitado de outro jeito. Cadastre e suba de novo.
              </p>
              <Linhas itens={previa.sem_cadastro} />
            </section>
          )}

          {previa.sem_resposta.length > 0 && (
            <section className="section">
              <h3>{previa.sem_resposta.length} estão no cadastro e não responderam</h3>
              <p className="field__hint">É a lista para cobrar.</p>
              <ul className="stack">
                {previa.sem_resposta.map((p) => (
                  <li key={p.id} className="row">
                    {p.nome} <span className="input--mono">{p.codigo_externo ?? ''}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* --- o texto livre, que nenhum campo captura --- */}

          {comObservacao.length > 0 && (
            <section className="section">
              <h3>{comObservacao.length} escreveram uma observação</h3>
              <p className="field__hint">
                O questionário não tem campo para o que está escrito aqui, e costuma haver restrição
                real. <strong>Nada disto é aplicado</strong> — leia e, se valer, cadastre à mão.
              </p>
              <ul className="stack">
                {comObservacao.map((r) => (
                  <li key={r.linha} className="callout">
                    <strong>{r.nome}</strong>
                    <p>{r.observacao}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* --- e por último o que deu certo --- */}

          <details className="section">
            <summary>
              {previa.casadas.length} respostas reconhecidas
              {gravado ? '' : ', prontas para gravar'}
            </summary>
            <Linhas itens={previa.casadas}>
              {(r) => `${r.professor_nome} (por ${r.como.toLowerCase()})`}
            </Linhas>
          </details>
        </>
      )}
    </>
  )
}
